import { InputBase, useTheme } from '@mui/material';
import React from 'react';

import type { QuickFilterDef, QuickFilterValue } from './FilterBar.props';
import { quickList, quickText } from './filterBarState';
import { controlEngagedSx } from './filterBarStyles';
import { MultiSelectDropdown } from './MultiSelectDropdown';

export interface FilterBarQuickFiltersProps {
  defs: readonly QuickFilterDef[];
  quick: Record<string, QuickFilterValue> | undefined;
  onChange: (key: string, value: QuickFilterValue) => void;
  inputSx: Record<string, unknown>;
}

/** Default width for a text slot; dropdowns size themselves to their label. */
const TEXT_SLOT_WIDTH = 140;

/**
 * Renders the configured quick-filter slots in array order.
 *
 * This is the config-driven replacement for `FilterBarBuiltInFields`, whose
 * slots were hard-coded to the Jobs vocabulary (Status / Job Type / Contract)
 * and so could not express, for example, Shipments' three text inputs and one
 * dropdown.
 */
export const FilterBarQuickFilters: React.FC<FilterBarQuickFiltersProps> = ({
  defs,
  quick,
  onChange,
  inputSx,
}) => {
  const theme = useTheme();

  return (
    <>
      {defs.map((def) => {
        // A hidden slot keeps its state but renders nothing — see `hidden` on
        // QuickFilterBase for why this is not the same as omitting it.
        if (def.hidden) return null;

        if (def.kind === 'multiselect') {
          return (
            <MultiSelectDropdown
              key={def.key}
              label={def.label}
              options={def.options}
              selected={quickList(quick, def.key)}
              onChange={(value) => onChange(def.key, value)}
              groups={def.groups}
            />
          );
        }

        // A slot holding text is "engaged": it takes the accent border, the same
        // signal a dropdown gives once something is picked. Across the strip that
        // makes the slots currently narrowing the list readable at a glance.
        const value = quickText(quick, def.key);

        return (
          <InputBase
            key={def.key}
            placeholder={def.label}
            value={value}
            onChange={(e) => onChange(def.key, e.target.value)}
            inputProps={{ 'aria-label': def.ariaLabel ?? `${def.label} filter` }}
            sx={{
              ...inputSx,
              ...(value ? controlEngagedSx(theme) : {}),
              width: def.width ?? TEXT_SLOT_WIDTH,
            }}
          />
        );
      })}
    </>
  );
};
